import { Link } from "react-router-dom";
import { CheckCircle2, Camera, Film, Sparkles, Plane, Mic, Image as ImageIcon } from "lucide-react";
import VideoCard from "../components/VideoCard";
import VideoRow from "../components/VideoRow";
import Reveal from "../components/Reveal";
import QuoteForm from "../components/QuoteForm";
import ClientMarquee from "../components/ClientMarquee";
import SEO from "../components/SEO";
import { serviceJsonLd } from "../data/seo";
import { VIDEO_PRODUCTION_FEATURES, HOME_ROWS } from "../data/content";

const INCLUDES = [
  "Concept development & scripting",
  "Pre-production planning & scheduling",
  "Filming with broadcast-quality kit",
  "Professional editing & motion graphics",
  "Multiple versions for socials, web & broadcast",
  "FREE stills photography (worth £800)",
  "Unlimited copyright usage of all delivered files",
];

const ICONS = [Mic, Camera, ImageIcon, Sparkles, Film, Plane];

export default function VideoProduction() {
  return (
    <main className="pt-28 pb-12" data-testid="video-production-page">
      <SEO
        title="Video Production Liverpool | Video Production Company | Yellow"
        description="Liverpool video production company since 2012. Filming, editing & motion graphics with FREE stills photography. 1,000+ films delivered for brands UK-wide. Fixed quote in 4 hrs."
        path="/video-production-in-liverpool"
        keywords="video production liverpool, liverpool video production, video production companies liverpool, video production company liverpool, corporate video liverpool, video production agency liverpool, video production services liverpool, videographers liverpool, commercial video liverpool"
        jsonLd={serviceJsonLd({
          name: "Video Production Liverpool",
          description:
            "Full-service video production company in Liverpool offering filming, editing, motion graphics, and free stills photography. Covering the entire UK.",
          path: "/video-production-in-liverpool",
        })}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Video Production", path: "/video-production-in-liverpool" },
        ]}
      />
      {/* Hero */}
      <section className="yv-container relative">
        <Reveal>
          <p className="yv-eyebrow">Video Production Liverpool</p>
          <h1 className="yv-h1 mt-3 max-w-5xl">
            <span style={{ color: "var(--yellow)" }}>Film and edit multiple videos</span> for your brand, with FREE stills photography included.
          </h1>
          <p className="text-white/70 max-w-3xl mt-6 text-lg leading-relaxed">
            Yellow is a <strong className="text-white">video production company</strong> that has filmed <strong className="text-white">over 1,000 videos</strong> for global brands, government agencies, universities, colleges, SMEs and charities. We can plan, film and edit your project in just <strong className="text-white">2 weeks</strong>. Based in Liverpool, covering the entire UK & Ireland.
          </p>
          <div className="flex gap-3 mt-8">
            <Link to="/contact" className="yv-btn yv-btn-primary">Get a quote</Link>
            <Link to="/showreel" className="yv-btn yv-btn-secondary">See the reel</Link>
          </div>
        </Reveal>
      </section>

      <ClientMarquee />

      {/* Featured Row */}
      <section className="yv-container mt-16">
        <VideoRow title="A snapshot of our work" eyebrow="Recent productions" videos={HOME_ROWS.recent.videos} testId="vp-row" />
      </section>

      {/* Features grid (the 6 capability shots from old site) */}
      <section className="yv-container mt-12">
        <Reveal>
          <p className="yv-eyebrow">What goes in your video</p>
          <h2 className="yv-h2 text-white mt-2 max-w-3xl">
            We can provide any (or all) of these in your videos…
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
          {VIDEO_PRODUCTION_FEATURES.map((f, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <Reveal key={f.id} delay={i * 60}>
                <div className="bg-white/[0.02] border border-white/10 rounded-md overflow-hidden" data-testid={`vp-feature-${f.id}`}>
                  <VideoCard video={{ id: f.id, title: f.title, youtube: f.youtube }} />
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="p-2 rounded-md bg-[var(--yellow)]/10 text-[var(--yellow)]">
                        <Icon size={18} />
                      </span>
                      <h3 className="yv-h3 text-white">{f.title}</h3>
                    </div>
                    <p className="text-white/65 text-sm leading-relaxed">{f.body}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Package contents + Form side-by-side */}
      <section className="yv-container mt-24 grid lg:grid-cols-2 gap-12 items-start">
        <Reveal>
          <p className="yv-eyebrow">Every Yellow video includes</p>
          <h2 className="yv-h2 text-white mt-2">Our packages include at least:</h2>
          <ul className="mt-8 space-y-3">
            {INCLUDES.map((i) => (
              <li key={i} className="flex items-start gap-3 text-white/80">
                <CheckCircle2 className="text-[var(--yellow)] mt-0.5 flex-shrink-0" size={20} />
                <span>{i}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={120}>
          <QuoteForm heading="Get a quote within 4 hours" />
        </Reveal>
      </section>
    </main>
  );
}
